import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/dashboard/admin-sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2, Briefcase } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { formatPlatformCurrencyExact } from "@/lib/currency";

interface PortfolioWithInvestor {
  _id: string;
  investorId: string;
  fundName: string;
  totalInvested: string;
  currentValue: string;
  returns: string;
  payout?: string;
  lockIn?: string;
  maturity?: string;
  inceptionDate: string;
  investor?: {
    firstName: string;
    lastName: string;
    clientId: string;
  };
}

export default function AdminInvestmentsPage() {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: portfolios, isLoading } = useQuery<PortfolioWithInvestor[]>({
    queryKey: ["/api/admin/portfolios"],
  });

  const { data: investors } = useQuery<any[]>({
    queryKey: ["/api/admin/investors"],
  });

  const enrichedPortfolios = portfolios?.map(p => {
    const investor = investors?.find(i => (i.id || i._id) === p.investorId);
    return {
      ...p,
      investor: investor ? {
        firstName: investor.firstName,
        lastName: investor.lastName,
        clientId: investor.clientId
      } : undefined
    };
  });

  const filteredPortfolios = enrichedPortfolios?.filter(p => {
    const searchStr = `${p.investor?.firstName} ${p.investor?.lastName} ${p.investor?.clientId} ${p.fundName}`.toLowerCase();
    return searchStr.includes(searchQuery.toLowerCase());
  }) || [];

  const totalPortfolioA = filteredPortfolios.reduce((sum, p) => sum + parseInt(p.currentValue || "0"), 0);
  const totalInvestedSum = filteredPortfolios.reduce((sum, p) => sum + parseInt(p.totalInvested || "0"), 0);

  const formatCurrency = (val: string | number) =>
    formatPlatformCurrencyExact(typeof val === "string" ? parseInt(val) : val);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AdminSidebar />
        <main className="flex-1 overflow-auto">
          <header className="flex h-16 items-center justify-between border-b px-6 sticky top-0 bg-background/80 backdrop-blur-md z-10">
            <div className="flex items-center gap-4">
              <SidebarTrigger />
              <div className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-accent" />
                <h1 className="text-xl font-semibold">Investment List A</h1>
              </div>
            </div>
          </header>

          <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
            {/* Summary Card */}
            <Card className="bg-accent/5 border-accent/20">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                    <div className="text-center md:text-left">
                      <p className="text-xs text-muted-foreground uppercase tracking-widest font-semibold mb-1">Total Portfolio A (Value)</p>
                      <h2 className="text-4xl font-extrabold text-accent leading-none tracking-tight">{formatCurrency(totalPortfolioA)}</h2>
                    </div>
                    <div className="h-12 w-px bg-accent/20 hidden md:block" />
                    <div className="text-center md:text-left opacity-80">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold mb-1">Total Invested (Principal)</p>
                      <p className="text-2xl font-bold">{formatCurrency(totalInvestedSum)}</p>
                    </div>
                  </div>
                  <div className="text-right hidden lg:block bg-background/50 px-4 py-2 rounded-lg border border-accent/10">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">Active Investments</p>
                    <p className="text-2xl font-black text-accent">{filteredPortfolios.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
              <div className="relative w-full max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by investor, ID or product..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left py-4 px-4 font-semibold text-muted-foreground">Date</th>
                        <th className="text-left py-4 px-4 font-semibold text-muted-foreground">Name of Investor</th>
                        <th className="text-left py-4 px-4 font-semibold text-muted-foreground">Investor ID</th>
                        <th className="text-left py-4 px-4 font-semibold text-muted-foreground">Product</th>
                        <th className="text-right py-4 px-4 font-semibold text-muted-foreground">Amount (INR)</th>
                        <th className="text-left py-4 px-4 font-semibold text-muted-foreground">Payout (INR)</th>
                        <th className="text-center py-4 px-4 font-semibold text-muted-foreground">Lock-in</th>
                        <th className="text-center py-4 px-4 font-semibold text-muted-foreground">Maturity</th>
                        <th className="text-right py-4 px-4 font-semibold text-muted-foreground">Returns (till date)</th>
                        <th className="text-right py-4 px-4 font-semibold text-muted-foreground">Total Portfolio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={10} className="py-20 text-center">
                            <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
                            <p className="mt-2 text-muted-foreground">Loading investments...</p>
                          </td>
                        </tr>
                      ) : filteredPortfolios.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="py-20 text-center text-muted-foreground">
                            No investments found.
                          </td>
                        </tr>
                      ) : (
                        filteredPortfolios.map((p) => (
                          <tr key={p._id} className="border-b hover:bg-muted/30 transition-colors">
                            <td className="py-4 px-4 whitespace-nowrap">
                              {new Date(p.inceptionDate).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "2-digit",
                              })}
                            </td>
                            <td className="py-4 px-4 font-medium uppercase text-xs">
                              {p.investor ? `${p.investor.firstName} ${p.investor.lastName}` : "Unknown"}
                            </td>
                            <td className="py-4 px-4 text-muted-foreground">
                              {p.investor?.clientId || "-"}
                            </td>
                            <td className="py-4 px-4">
                              <Badge variant="secondary" className="font-normal text-[10px] uppercase tracking-tight">
                                {p.fundName}
                              </Badge>
                            </td>
                            <td className="py-4 px-4 text-right font-semibold">
                              {formatCurrency(p.totalInvested)}
                            </td>
                            <td className="py-4 px-4 text-xs">
                              {p.payout || "Lum Sum at end"}
                            </td>
                            <td className="py-4 px-4 text-center text-xs">
                              {p.lockIn || "-"}
                            </td>
                            <td className="py-4 px-4 text-center text-xs">
                              {p.maturity || "-"}
                            </td>
                            <td className="py-4 px-4 text-right text-green-600 dark:text-green-400 font-medium">
                              {formatCurrency(p.returns)}
                            </td>
                            <td className="py-4 px-4 text-right font-bold">
                              {formatCurrency(p.currentValue)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
